import { FLAGS } from '@gozoki/feature-flags'
import { useQueries, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { CartesianGrid, Legend, Line, LineChart, XAxis, YAxis } from 'recharts'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { AppTabs, type AppTab } from '~/components/new/app-ui/app-tabs'
import { Title } from '~/components/new/app-ui/title'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '~/components/ui/chart'
import { Input } from '~/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { Switch } from '~/components/ui/switch'
import { TabsContent } from '~/components/ui/tabs'
import { useFeatureFlag } from '~/hooks/useFeatureFlag'
import { api } from '~/lib/tuyau'
import { formatCentsToEuro } from '~/lib/utils'
import { PrevisionsStoreSelector } from './previsions-store-selector'
import { previsionsSearchSchema, type PrevisionsSearch } from './search-schema'

export const Route = createFileRoute('/_authenticated/previsions/')({
  validateSearch: previsionsSearchSchema,
  component: PrevisionsPage,
})

const HORIZON_OPTIONS = [6, 12, 24]

const MONTH_LABELS = [
  'Jan',
  'Fév',
  'Mar',
  'Avr',
  'Mai',
  'Juin',
  'Juil',
  'Août',
  'Sep',
  'Oct',
  'Nov',
  'Déc',
]

const STORE_COLORS = [
  '#6a9560',
  '#ffa400',
  '#4f7cac',
  '#c1666b',
  '#8e7dbe',
  '#57a773',
  '#e26d5c',
  '#3d5a80',
  '#e9c46a',
  '#9b5de5',
]
const TOTAL_COLOR = '#111827'

const monthKey = (year: number, month: number) => `${year}-${String(month).padStart(2, '0')}`
const formatApe = (value: number) => `${(value * 100).toFixed(1)} %`

const ageCurveAt = (curve: number[], age: number) => {
  if (curve.length === 0) return 1
  const index = Math.max(1, Math.round(age)) - 1
  return index < curve.length ? curve[index] : curve[curve.length - 1]
}

interface ForecastData {
  storeId: number
  firstActivityDate: string | null
  baseDay: number | null
  tables: {
    ageCurve: number[]
    monthlyCoefficients: number[]
    dayTypeWeights: { worked: number; weekend: number; holiday: number }
  }
  observed: { year: number; month: number; monthlyCa: number }[]
  points: { year: number; month: number; ageMonths: number; predictedCa: number }[]
}

type WideRow = Record<string, number | null | string>

const ageOfMonth = (firstActivityDate: string, year: number, month: number) =>
  (year - Number(firstActivityDate.slice(0, 4))) * 12 +
  (month - Number(firstActivityDate.slice(5, 7))) +
  1

interface StoreKey {
  storeId: number
  realKey: string
  forecastKey: string
}

interface WideChart {
  rows: WideRow[]
  storeKeys: StoreKey[]
}

const storeKeysOf = (forecasts: ForecastData[]): StoreKey[] =>
  forecasts.map((f) => ({
    storeId: f.storeId,
    realKey: `s${f.storeId}`,
    forecastKey: `s${f.storeId}f`,
  }))

const buildCalendarWide = (forecasts: ForecastData[]): WideChart => {
  const storeKeys = storeKeysOf(forecasts)
  const rowMap = new Map<string, WideRow>()
  const rowFor = (key: string) => {
    const existing = rowMap.get(key)
    if (existing) return existing
    const created: WideRow = { key }
    rowMap.set(key, created)
    return created
  }
  for (const forecast of forecasts) {
    const realKey = `s${forecast.storeId}`
    const forecastKey = `s${forecast.storeId}f`
    let lastObservedKey: string | null = null
    for (const observed of forecast.observed) {
      const key = monthKey(observed.year, observed.month)
      rowFor(key)[realKey] = observed.monthlyCa
      if (lastObservedKey == null || key > lastObservedKey) lastObservedKey = key
    }
    for (const point of forecast.points) {
      rowFor(monthKey(point.year, point.month))[forecastKey] = point.predictedCa
    }
    if (lastObservedKey != null) {
      rowFor(lastObservedKey)[forecastKey] = rowFor(lastObservedKey)[realKey]
    }
  }
  const reals = storeKeys.map((sk) => sk.realKey)
  const forecastsK = storeKeys.map((sk) => sk.forecastKey)
  for (const row of rowMap.values()) {
    const realValues = reals.map((k) => row[k])
    row.total = realValues.every((v) => v != null)
      ? realValues.reduce((sum: number, v) => sum + (v as number), 0)
      : null
    const forecastValues = forecastsK.map((k) => row[k])
    row.totalf = forecastValues.every((v) => v != null)
      ? forecastValues.reduce((sum: number, v) => sum + (v as number), 0)
      : null
  }
  const rows = [...rowMap.values()].sort((a, b) => String(a.key).localeCompare(String(b.key)))
  return { rows, storeKeys }
}

const averageOfNumbers = (values: (number | null | string | undefined)[]) => {
  const nums = values.filter((v): v is number => typeof v === 'number')
  return nums.length > 0 ? nums.reduce((sum, v) => sum + v, 0) / nums.length : null
}

const buildCohortWide = (forecasts: ForecastData[]): WideChart => {
  const storeKeys = storeKeysOf(forecasts)
  const rowMap = new Map<number, WideRow>()
  const rowFor = (age: number) => {
    const existing = rowMap.get(age)
    if (existing) return existing
    const created: WideRow = { key: String(age) }
    rowMap.set(age, created)
    return created
  }
  for (const forecast of forecasts) {
    if (forecast.firstActivityDate == null) continue
    const realKey = `s${forecast.storeId}`
    const forecastKey = `s${forecast.storeId}f`
    let lastObservedAge: number | null = null
    for (const observed of forecast.observed) {
      const age = ageOfMonth(forecast.firstActivityDate, observed.year, observed.month)
      rowFor(age)[realKey] = observed.monthlyCa
      if (lastObservedAge == null || age > lastObservedAge) lastObservedAge = age
    }
    for (const point of forecast.points) {
      rowFor(point.ageMonths)[forecastKey] = point.predictedCa
    }
    if (lastObservedAge != null) {
      rowFor(lastObservedAge)[forecastKey] = rowFor(lastObservedAge)[realKey]
    }
  }
  for (const row of rowMap.values()) {
    row.total = averageOfNumbers(storeKeys.map((sk) => row[sk.realKey]))
    row.totalf = averageOfNumbers(storeKeys.map((sk) => row[sk.forecastKey]))
  }
  const rows = [...rowMap.entries()].sort((a, b) => a[0] - b[0]).map(([, row]) => row)
  return { rows, storeKeys }
}

function PrevisionsPage() {
  const enabled = useFeatureFlag(FLAGS.CAForecasting)
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const [hidden, setHidden] = useState<Set<string>>(new Set())

  const setSearch = (patch: Partial<PrevisionsSearch>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }) })

  const storeIds = search.storeIds ?? []

  const monthlyQuery =
    search.monthly != null && search.monthly.length === 12 ? { monthly: search.monthly } : {}
  const poolIds = search.poolIds ?? []
  const poolQuery = poolIds.length > 0 ? { poolStoreIds: poolIds } : {}
  const windowQuery = search.window !== 3 ? { window: search.window } : {}
  const commonQuery = {
    seasonality: search.seasonality,
    ...monthlyQuery,
    ...poolQuery,
    ...windowQuery,
  }

  const { data: boutiques = [] } = useQuery(
    api.stores.index.queryOptions({ query: { storeType: 'BOUTIQUE' } }),
  )

  const labelOf = (storeId: number) => {
    const store = boutiques.find((item) => item.id === storeId)
    return store?.shortLabel ?? store?.label ?? `#${storeId}`
  }

  const forecastResults = useQueries({
    queries: storeIds.map((id) =>
      api.forecast.forecastStore.queryOptions(
        { params: { id }, query: { horizon: search.horizon, ...commonQuery } },
        { enabled },
      ),
    ),
  })

  const backtestQuery = useQuery(
    api.forecast.backtestAggregate.queryOptions(
      { query: { storeIds, horizon: 6, ...commonQuery } },
      { enabled: enabled && storeIds.length > 0 && search.tab === 'backtest' },
    ),
  )

  if (!enabled) {
    return (
      <>
        <AppHeader>
          <Title>Prévisions</Title>
        </AppHeader>
        <AppContainer>
          <p className="text-muted-foreground">Fonctionnalité non activée.</p>
        </AppContainer>
      </>
    )
  }

  const forecasts = forecastResults
    .map((result) => result.data)
    .filter((data): data is NonNullable<typeof data> => data != null && data.baseDay != null)
  const isForecastPending =
    storeIds.length > 0 && forecastResults.some((result) => result.isPending)
  const referenceForecast = forecasts[0]
  const totalExecutionMs = forecasts.reduce((sum, forecast) => sum + forecast.executionMs, 0)
  const seasonalityFallback = forecasts.some((forecast) => forecast.seasonalityFallback)

  const { rows, storeKeys } =
    search.view === 'cohorte' ? buildCohortWide(forecasts) : buildCalendarWide(forecasts)
  const showTotal = storeKeys.length > 1
  const totalLabel = search.view === 'cohorte' ? 'Moyenne' : 'Total'

  const chartConfig = Object.fromEntries([
    ...storeKeys.flatMap((sk, index) => {
      const color = STORE_COLORS[index % STORE_COLORS.length]
      return [
        [sk.realKey, { label: labelOf(sk.storeId), color }],
        [sk.forecastKey, { label: `${labelOf(sk.storeId)} (prévu)`, color }],
      ]
    }),
    ['total', { label: totalLabel, color: TOTAL_COLOR }],
    ['totalf', { label: `${totalLabel} (prévu)`, color: TOTAL_COLOR }],
  ])

  const toggle = (dataKey: string) =>
    setHidden((prev) => {
      const next = new Set(prev)
      if (next.has(dataKey)) next.delete(dataKey)
      else next.add(dataKey)
      return next
    })

  const backtest = backtestQuery.data

  const singleForecast = forecasts.length === 1 ? forecasts[0] : null

  const tabs: AppTab<'forecast' | 'backtest'>[] = [
    { value: 'forecast', label: 'Prévision' },
    { value: 'backtest', label: 'Backtest' },
  ]

  return (
    <>
      <AppHeader>
        <Title>Prévisions</Title>
      </AppHeader>
      <AppContainer className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">Boutiques à prédire</span>
            <PrevisionsStoreSelector
              title="Boutiques à prédire"
              selectedIds={storeIds}
              onApply={(ids) => setSearch({ storeIds: ids.length > 0 ? ids : undefined })}
              placeholder="Choisir des boutiques"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">Réseau de référence (fit)</span>
            <PrevisionsStoreSelector
              title="Réseau de référence (fit)"
              selectedIds={poolIds}
              onApply={(ids) => setSearch({ poolIds: ids.length > 0 ? ids : undefined })}
              placeholder="Tout le réseau"
              copyStoreIds={storeIds}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">Horizon</span>
            <Select
              value={search.horizon.toString()}
              onValueChange={(value) => setSearch({ horizon: Number(value) })}
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HORIZON_OPTIONS.map((horizon) => (
                  <SelectItem key={horizon} value={horizon.toString()}>
                    {horizon} mois
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">Vue</span>
            <Select
              value={search.view}
              onValueChange={(value) =>
                setSearch({ view: value === 'cohorte' ? 'cohorte' : 'calendaire' })
              }
            >
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="calendaire">Calendaire</SelectItem>
                <SelectItem value="cohorte">Cohorte (ancienneté)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pb-2">
            <Switch
              id="seasonality"
              checked={search.seasonality}
              onCheckedChange={(checked) => setSearch({ seasonality: checked })}
            />
            <label htmlFor="seasonality" className="text-sm">
              Saisonnalité
            </label>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">Fenêtre Base_jour (mois)</span>
            <Input
              key={search.window}
              type="number"
              step="1"
              min="1"
              max="24"
              className="w-28"
              defaultValue={search.window}
              title="Nombre de derniers mois observés utilisés pour mesurer la Base_jour (médiane des mois déshabillés)."
              onBlur={(event) => {
                const raw = event.target.value
                setSearch({ window: raw === '' ? 3 : Math.min(24, Math.max(1, Number(raw))) })
              }}
            />
          </div>
        </div>

        {seasonalityFallback && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
            Réseau de référence trop restreint pour estimer la saisonnalité : les 12 coefficients
            mensuels ont été repris sur tout le réseau. Élargissez le réseau de référence pour une
            saisonnalité propre au groupe.
          </div>
        )}

        {referenceForecast?.tables != null && (
          <Card>
            <CardHeader>
              <CardTitle>Coefficients du modèle</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-5">
              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 text-sm font-medium">
                    Coefficients mensuels — éditables (appliqués si la saisonnalité est active)
                    {search.monthly?.length === 12 && (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-normal text-amber-700">
                        Modifiés manuellement — les valeurs affichées ne sont pas le fit auto
                      </span>
                    )}
                  </span>
                  {search.monthly?.length === 12 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSearch({ monthly: undefined })}
                    >
                      Réinitialiser le fit auto
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {MONTH_LABELS.map((label, index) => {
                    const fitted = referenceForecast.tables.monthlyCoefficients
                    const value = search.monthly?.[index] ?? fitted[index]
                    return (
                      <div key={label} className="flex flex-col gap-1">
                        <span className="text-xs text-muted-foreground">{label}</span>
                        <Input
                          key={`${index}-${fitted[index]}`}
                          type="number"
                          step="0.01"
                          className="w-20"
                          defaultValue={value.toFixed(2)}
                          onBlur={(event) => {
                            const raw = event.target.value
                            const editedValue = raw === '' ? fitted[index] : Number(raw)
                            const next = fitted.map((fit, month) => {
                              if (month === index) return editedValue
                              return search.monthly?.[month] ?? fit
                            })
                            setSearch({ monthly: next })
                          }}
                        />
                      </div>
                    )
                  })}
                </div>
              </div>

              <div>
                <div className="mb-2 text-sm font-medium">
                  Coefficient de montée en puissance R(âge) — mesuré
                </div>
                <ChartContainer
                  config={{ r: { label: 'R', color: '#6a9560' } }}
                  className="h-[200px] w-full"
                >
                  <LineChart
                    accessibilityLayer
                    data={referenceForecast.tables.ageCurve.map((r, index) => ({
                      age: index + 1,
                      r,
                    }))}
                    margin={{ left: 8, right: 8 }}
                  >
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="age"
                      tickLine={false}
                      axisLine={false}
                      tickMargin={8}
                      tickFormatter={(value: number) => `M${value}`}
                    />
                    <YAxis tickLine={false} axisLine={false} width={40} domain={[0, 'auto']} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Line dataKey="r" stroke="var(--color-r)" strokeWidth={2} dot={false} />
                  </LineChart>
                </ChartContainer>
              </div>

              <div>
                <div className="mb-2 text-sm font-medium">Types de jours — mesurés</div>
                <div className="flex flex-wrap gap-4 text-sm">
                  <span>
                    Travaillé :{' '}
                    <strong>{referenceForecast.tables.dayTypeWeights.worked.toFixed(2)}</strong>
                  </span>
                  <span>
                    Week-end :{' '}
                    <strong>{referenceForecast.tables.dayTypeWeights.weekend.toFixed(2)}</strong>
                  </span>
                  <span>
                    Férié :{' '}
                    <strong>{referenceForecast.tables.dayTypeWeights.holiday.toFixed(2)}</strong>
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <AppTabs tabs={tabs} value={search.tab} onTabChange={(tab) => setSearch({ tab })}>
          <TabsContent value="forecast">
            {(() => {
              if (storeIds.length === 0) {
                return (
                  <p className="text-muted-foreground">Sélectionnez une ou plusieurs boutiques.</p>
                )
              }
              if (isForecastPending) {
                return <p className="text-muted-foreground">Calcul en cours…</p>
              }
              if (forecasts.length === 0) {
                return (
                  <p className="text-muted-foreground">
                    Pas assez de données pour les boutiques sélectionnées.
                  </p>
                )
              }
              return (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    <span>{forecasts.length} boutique(s)</span>
                    <span>Panel de fit : {referenceForecast?.poolSize} boutiques</span>
                    <span>Calcul : {totalExecutionMs} ms</span>
                  </div>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">
                        {search.view === 'cohorte'
                          ? 'CA HT par ancienneté (M1 = ouverture)'
                          : 'CA HT mensuel'}{' '}
                        <span className="font-normal text-muted-foreground">
                          — trait plein = réel, pointillé = prévu · clique la légende pour filtrer
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ChartContainer config={chartConfig} className="h-[420px] w-full">
                        <LineChart accessibilityLayer data={rows} margin={{ left: 12, right: 12 }}>
                          <CartesianGrid vertical={false} />
                          <XAxis
                            dataKey="key"
                            tickLine={false}
                            axisLine={false}
                            tickMargin={8}
                            tickFormatter={(value: string) =>
                              search.view === 'cohorte' ? `M${value}` : value
                            }
                          />
                          <YAxis
                            tickLine={false}
                            axisLine={false}
                            width={72}
                            tickFormatter={(value: number) => formatCentsToEuro(value)}
                          />
                          <ChartTooltip
                            content={
                              <ChartTooltipContent
                                formatter={(value, name, item) => {
                                  if (value == null || Number.isNaN(Number(value))) return null
                                  const key = String(name)
                                  const row = (item as { payload?: Record<string, unknown> })
                                    ?.payload
                                  if (key.endsWith('f') && row?.[key.slice(0, -1)] != null) {
                                    return null
                                  }
                                  const label =
                                    (chartConfig[key] as { label?: string } | undefined)?.label ??
                                    key
                                  return (
                                    <div className="flex w-full items-center gap-2">
                                      <span
                                        className="size-2 shrink-0 rounded-full"
                                        style={{
                                          backgroundColor: (item as { color?: string })?.color,
                                        }}
                                      />
                                      <span className="flex-1 text-muted-foreground">{label}</span>
                                      <span className="font-medium">
                                        {formatCentsToEuro(Number(value))}
                                      </span>
                                    </div>
                                  )
                                }}
                              />
                            }
                          />
                          <Legend
                            onClick={(payload) => toggle(String(payload.dataKey))}
                            formatter={(_value, entry) => {
                              const dataKey = String(
                                (entry as unknown as { dataKey?: string }).dataKey ?? '',
                              )
                              const label =
                                dataKey === 'total'
                                  ? totalLabel
                                  : labelOf(Number(dataKey.replace('s', '')))
                              return (
                                <span
                                  style={{
                                    opacity: hidden.has(dataKey) ? 0.4 : 1,
                                    cursor: 'pointer',
                                  }}
                                >
                                  {label}
                                </span>
                              )
                            }}
                          />
                          {storeKeys.map((sk, index) => {
                            const color = STORE_COLORS[index % STORE_COLORS.length]
                            const hide = hidden.has(sk.realKey)
                            return [
                              <Line
                                key={sk.realKey}
                                dataKey={sk.realKey}
                                stroke={color}
                                strokeWidth={1.5}
                                dot={false}
                                connectNulls
                                hide={hide}
                              />,
                              <Line
                                key={sk.forecastKey}
                                dataKey={sk.forecastKey}
                                stroke={color}
                                strokeWidth={1.5}
                                strokeDasharray="5 4"
                                dot={false}
                                connectNulls
                                legendType="none"
                                hide={hide}
                              />,
                            ]
                          })}
                          {showTotal && [
                            <Line
                              key="total"
                              dataKey="total"
                              stroke={TOTAL_COLOR}
                              strokeWidth={2.5}
                              dot={false}
                              connectNulls
                              hide={hidden.has('total')}
                            />,
                            <Line
                              key="totalf"
                              dataKey="totalf"
                              stroke={TOTAL_COLOR}
                              strokeWidth={2.5}
                              strokeDasharray="5 4"
                              dot={false}
                              connectNulls
                              legendType="none"
                              hide={hidden.has('total')}
                            />,
                          ]}
                        </LineChart>
                      </ChartContainer>
                    </CardContent>
                  </Card>

                  {singleForecast?.baseDay != null && (
                    <Card>
                      <CardHeader>
                        <CardTitle>
                          Détail du calcul — {labelOf(singleForecast.storeId)} (CA prévu = Base_jour
                          × R(âge) × coeff mois × jours équivalents)
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="mb-3 text-sm text-muted-foreground">
                          Base_jour = {formatCentsToEuro(singleForecast.baseDay)} / jour travaillé
                        </div>
                        <div className="mb-4 rounded-md border bg-muted/30 p-3">
                          <div className="mb-2 text-xs font-medium">
                            Mesure de la Base_jour = médiane des{' '}
                            {singleForecast.baseDayWindowMonths} derniers mois « déshabillés » (CA
                            du mois ÷ [R(âge) × coeff mois × jours équivalents])
                          </div>
                          <table className="w-full text-xs">
                            <thead className="text-muted-foreground">
                              <tr className="border-b">
                                <th className="py-1 text-left">Mois</th>
                                <th className="py-1 text-right">Âge</th>
                                <th className="py-1 text-right">CA réel</th>
                                <th className="py-1 text-right">Base_jour déshabillée</th>
                              </tr>
                            </thead>
                            <tbody>
                              {singleForecast.baseDayEstimates.map((estimate) => (
                                <tr
                                  key={`${estimate.year}-${estimate.month}`}
                                  className="border-b last:border-0"
                                >
                                  <td className="py-1 text-left">
                                    {MONTH_LABELS[estimate.month - 1]} {estimate.year}
                                  </td>
                                  <td className="py-1 text-right">{estimate.ageMonths}</td>
                                  <td className="py-1 text-right">
                                    {formatCentsToEuro(estimate.monthlyCa)}
                                  </td>
                                  <td className="py-1 text-right">
                                    {estimate.perDay != null
                                      ? formatCentsToEuro(estimate.perDay)
                                      : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <table className="w-full text-sm">
                          <thead className="text-muted-foreground">
                            <tr className="border-b">
                              <th className="py-2 text-left">Mois</th>
                              <th className="py-2 text-right">Âge</th>
                              <th className="py-2 text-right">R(âge)</th>
                              <th className="py-2 text-right">Coeff mois</th>
                              <th className="py-2 text-right">Jours équiv.</th>
                              <th className="py-2 text-right">CA prévu</th>
                            </tr>
                          </thead>
                          <tbody>
                            {singleForecast.points.map((point) => {
                              const r = ageCurveAt(singleForecast.tables.ageCurve, point.ageMonths)
                              const monthCoeff =
                                singleForecast.tables.monthlyCoefficients[point.month - 1] ?? 1
                              const composition =
                                point.predictedCa / (singleForecast.baseDay! * r * monthCoeff)
                              return (
                                <tr
                                  key={`${point.year}-${point.month}`}
                                  className="border-b last:border-0"
                                >
                                  <td className="py-2">{monthKey(point.year, point.month)}</td>
                                  <td className="py-2 text-right font-medium">
                                    M{point.ageMonths}
                                  </td>
                                  <td className="py-2 text-right">{r.toFixed(2)}</td>
                                  <td className="py-2 text-right">{monthCoeff.toFixed(2)}</td>
                                  <td className="py-2 text-right">{composition.toFixed(1)}</td>
                                  <td className="py-2 text-right font-medium">
                                    {formatCentsToEuro(point.predictedCa)}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )
            })()}
          </TabsContent>

          <TabsContent value="backtest">
            {(() => {
              if (storeIds.length === 0) {
                return (
                  <p className="text-muted-foreground">Sélectionnez une ou plusieurs boutiques.</p>
                )
              }
              if (backtestQuery.isPending) {
                return <p className="text-muted-foreground">Backtest en cours…</p>
              }
              if (backtest == null || backtest.overallMedianApe == null) {
                return (
                  <p className="text-muted-foreground">Historique insuffisant pour un backtest.</p>
                )
              }
              return (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    <span>
                      Erreur médiane globale :{' '}
                      <strong className="text-foreground">
                        {formatApe(backtest.overallMedianApe)}
                      </strong>
                    </span>
                    <span>{backtest.storesCount} boutique(s) agrégée(s)</span>
                    <span>{backtest.sampleCount} prédictions testées</span>
                    <span>Calcul : {backtest.executionMs} ms</span>
                  </div>
                  <Card>
                    <CardHeader>
                      <CardTitle>
                        Erreur par horizon — rolling-origin sur le CA agrégé (périmètre constant)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <table className="w-full text-sm">
                        <thead className="text-muted-foreground">
                          <tr className="border-b">
                            <th className="py-2 text-left">Horizon</th>
                            <th className="py-2 text-right">APE médian</th>
                            <th className="py-2 text-right">APE moyen</th>
                            <th className="py-2 text-right">Échantillons</th>
                          </tr>
                        </thead>
                        <tbody>
                          {backtest.perHorizon.map((entry) => (
                            <tr key={entry.horizon} className="border-b last:border-0">
                              <td className="py-2">+{entry.horizon} mois</td>
                              <td className="py-2 text-right font-medium">
                                {formatApe(entry.medianApe)}
                              </td>
                              <td className="py-2 text-right">{formatApe(entry.meanApe)}</td>
                              <td className="py-2 text-right">{entry.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                </div>
              )
            })()}
          </TabsContent>
        </AppTabs>
      </AppContainer>
    </>
  )
}
